import React, { useState } from "react";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { Table, Spin, Empty, Button, Select, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import Chart from "react-apexcharts";
import { useQuery } from "react-query";
import { serverUrl } from "../../../../../nestserver";
import {
  AiOutlineDollarCircle,
  AiOutlineCheckCircle,
  AiOutlineExclamationCircle,
  AiOutlineCloseCircle,
} from "react-icons/ai";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ExpenseDashboard = () => {
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);

  const categoriesList = ["Supplies", "Maintenance", "Utility", "Events", "Food & Beverage", "Salary", "Other"];

  // ─── Query Fetchers ────────────────────────────────────────────────────────
  const fetchSummary = async () => {
    const params = {};
    if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }
    const res = await axios.get(`${serverUrl}/expenses/analytics/summary`, {
      params,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data?.result || {};
  };

  const fetchCategories = async () => {
    const params = {};
    if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }
    const res = await axios.get(`${serverUrl}/expenses/analytics/category`, {
      params,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data?.result || [];
  };

  const fetchTrend = async () => {
    const params = {};
    if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }
    const res = await axios.get(`${serverUrl}/expenses/analytics/monthly-trend`, {
      params,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data?.result || [];
  };

  const fetchCreators = async () => {
    const params = {};
    if (categoryFilter && categoryFilter !== "all") params.category = categoryFilter;
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].format("YYYY-MM-DD");
      params.endDate = dateRange[1].format("YYYY-MM-DD");
    }
    const res = await axios.get(`${serverUrl}/expenses/analytics/creator`, {
      params,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data?.result || [];
  };

  // ─── React Query Hooks ──────────────────────────────────────────────────────
  const summaryQuery = useQuery(["expenses-summary", categoryFilter, dateRange], fetchSummary);
  const categoriesQuery = useQuery(["expenses-category", categoryFilter, dateRange], fetchCategories);
  const trendQuery = useQuery(["expenses-trend", categoryFilter, dateRange], fetchTrend);
  const creatorsQuery = useQuery(["expenses-creator", categoryFilter, dateRange], fetchCreators);

  const isLoading =
    summaryQuery.isLoading ||
    categoriesQuery.isLoading ||
    trendQuery.isLoading ||
    creatorsQuery.isLoading;

  const handleRefresh = () => {
    summaryQuery.refetch();
    categoriesQuery.refetch();
    trendQuery.refetch();
    creatorsQuery.refetch();
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val || 0);

  // ─── Charts Config ──────────────────────────────────────────────────────────
  const trendChartConfig = {
    options: {
      chart: {
        id: "monthly-spending-trend-bar",
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
        redrawOnParentResize: false,
      },
      colors: ["#f97316"],
      plotOptions: {
        bar: { columnWidth: "50%", borderRadius: 4 },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: (trendQuery.data || []).map((d) => d.monthName),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "11px", fontWeight: 500 } },
      },
      yaxis: {
        labels: { formatter: (v) => formatCurrency(v) },
      },
      tooltip: {
        y: { formatter: (v) => formatCurrency(v) },
      },
      grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
    },
    series: [
      {
        name: "Spent Amount",
        data: (trendQuery.data || []).map((d) => d.totalAmount),
      },
    ],
  };

  const donutChartConfig = {
    options: {
      chart: { id: "expenses-category-donut", redrawOnParentResize: false },
      labels: (categoriesQuery.data || []).map((d) => d.category),
      colors: ["#f97316", "#3b82f6", "#10b981", "#8b5cf6", "#ec4899", "#f59e0b", "#64748b"],
      legend: { position: "bottom", fontWeight: 600 },
      plotOptions: {
        pie: {
          customScale: 0.92,
          donut: {
            size: "60%",
            labels: {
              show: true,
              name: { show: true, fontSize: "13px", fontWeight: 600 },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 700,
                formatter: (val) => formatCurrency(val),
              },
              total: {
                show: true,
                label: "Total Spent",
                color: "#64748b",
                fontWeight: 700,
                formatter: () => {
                  const sum = (categoriesQuery.data || []).reduce((acc, curr) => acc + curr.totalAmount, 0);
                  return formatCurrency(sum);
                },
              },
            },
          },
        },
      },
      stroke: { width: 2 },
    },
    series: (categoriesQuery.data || []).map((d) => d.totalAmount),
  };

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const creatorColumns = [
    {
      title: "Name",
      dataIndex: "creatorName",
      key: "creatorName",
      className: "font-semibold text-slate-800",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <span className="px-2.5 py-0.5 bg-slate-100 rounded text-xs font-semibold text-slate-600 uppercase">
          {role}
        </span>
      ),
    },
    {
      title: "Requests Count",
      dataIndex: "count",
      key: "count",
      align: "center",
      className: "text-slate-600 font-medium",
    },
    {
      title: "Total Spent",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      render: (amt) => (
        <span className="font-bold text-orange-500">
          {formatCurrency(amt)}
        </span>
      ),
    },
  ];

  return (
    <div className="bg-slate-50 p-8 rounded-lg border pt-4">
      {/* Filters Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" style={{ marginBottom: "32px" }}>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Expenses Dashboard</h2>
          <p className="text-slate-500 text-sm mt-0.5">Real-time analytical graphs and spending metrics.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Select
            placeholder="All Categories"
            style={{ minWidth: 160 }}
            value={categoryFilter}
            onChange={(val) => setCategoryFilter(val)}
          >
            <Option value="all">All Categories</Option>
            {categoriesList.map((cat) => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>

          <RangePicker
            onChange={(dates) => setDateRange(dates || [])}
            className="rounded-lg shadow-sm border-slate-200"
          />

          <Tooltip title="Refresh Dashboard">
            <Button
              type="default"
              shape="circle"
              icon={<ReloadOutlined className={isLoading ? "animate-spin" : ""} />}
              onClick={handleRefresh}
              className="flex items-center justify-center border-orange-500 text-orange-500 hover:bg-orange-50"
            />
          </Tooltip>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-96">
          <Spin size="large" tip="Loading spending metrics..." />
        </div>
      ) : (
        <>
          {/* KPI Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" style={{ marginBottom: "32px" }}>
            {/* Total Budget Submitted */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Total Requested</span>
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                  <AiOutlineDollarCircle size={20} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-800">
                {formatCurrency(summaryQuery.data?.totalAmount)}
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                {summaryQuery.data?.totalCount || 0} expense requests
              </p>
            </div>

            {/* Total Approved */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Approved</span>
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                  <AiOutlineCheckCircle size={20} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-emerald-600">
                {formatCurrency(summaryQuery.data?.approvedAmount)}
              </h3>
              <p className="text-emerald-600 text-xs font-semibold mt-1">
                {summaryQuery.data?.approvedCount || 0} requests approved
              </p>
            </div>

            {/* Total Pending */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Pending</span>
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                  <AiOutlineExclamationCircle size={20} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-amber-600">
                {formatCurrency(summaryQuery.data?.pendingAmount)}
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                {summaryQuery.data?.pendingCount || 0} requests pending
              </p>
            </div>

            {/* Total Rejected */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Rejected</span>
                <div className="p-2 bg-rose-50 text-rose-600 rounded-lg">
                  <AiOutlineCloseCircle size={20} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-rose-600">
                {formatCurrency(summaryQuery.data?.rejectedAmount)}
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                {summaryQuery.data?.rejectedCount || 0} requests rejected
              </p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" style={{ marginBottom: "32px" }}>
            {/* Monthly Trend */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 lg:col-span-2">
              <h3 className="text-lg font-bold text-slate-800">Monthly Spending Trend</h3>
              <p className="text-slate-400 text-xs mb-4">Total expenditure across each month of the current year.</p>

              {(trendQuery.data || []).length > 0 ? (
                <Chart
                  options={trendChartConfig.options}
                  series={trendChartConfig.series}
                  type="bar"
                  height={300}
                />
              ) : (
                <div className="flex justify-center items-center h-72">
                  <Empty description="No spending trends found" />
                </div>
              )}
            </div>

            {/* Category Pie Chart */}
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
              <h3 className="text-lg font-bold text-slate-800 mb-1">Category Breakdown</h3>
              <p className="text-slate-400 text-xs mb-4">Distribution of spending across different categories.</p>

              {(categoriesQuery.data || []).length > 0 ? (
                <Chart
                  options={donutChartConfig.options}
                  series={donutChartConfig.series}
                  type="donut"
                  height={280}
                />
              ) : (
                <div className="flex justify-center items-center h-72">
                  <Empty description="No category spending recorded" />
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard/Creator Spending List */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
            <h3 className="text-lg font-bold text-slate-800">Spending Breakdown by Staff</h3>
            <p className="text-slate-400 text-xs mb-4">Total requested expenses grouped by submitting member.</p>

            <Table
              dataSource={creatorsQuery.data || []}
              columns={creatorColumns}
              pagination={{ pageSize: 5 }}
              rowKey="creatorId"
              size="small"
              locale={{
                emptyText: <Empty description="No spending recorded" />,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseDashboard;
